/**
 * AI Variant Configuration
 *
 * New structure separates time frames from AI models:
 * - Time Frames: Fast (2000ms), Standard (7000ms), Deep (15000ms)
 * - Models: Gaia (Priority), Sentinel (Minimax), Alchemist (MCTS), Zenith (Hybrid)
 */

import { AIPlayerType } from "./IAIPlayer";
import { MinimaxModel } from "../MinimaxAI";

// Time frame enum
export enum TimeFrame {
  FAST = "fast",
  STANDARD = "standard",
  DEEP = "deep",
}

// AI Model enum
export enum AIModel {
  ZSTR = "zstr",           // Zodiac Astra
  ZSTR_BLIND = "zstr-blind",
  GAIA = "gaia",           // Priority AI
  SENTINEL = "sentinel",   // Minimax AI
  ALCHEMIST = "alchemist", // MCTS AI
  ZENITH = "zenith",       // Hybrid AI
  TRAPPER = "trapper",     // TBD
  HUNTER = "hunter",       // TBD
  WISP = "wisp",           // TBD
  WEAVER = "weaver",       // TBD
  CREATURE = "creature",   // Priority + Minimax
  ARROW = "arrow",         // Race-focused Minimax
}

// Legacy variant enum for backward compatibility
export enum AIVariant {
  // Player
  PLAYER = "player",

  // Priority AI (Dion's AI) - Gaia
  FLORA = "flora",         // 2000ms
  PRIMA = "prima",         // 7000ms
  GAIA = "gaia",           // 15000ms

  // Minimax AI - Sentinel
  DRONE = "drone",         // 2000ms
  SENTINEL = "sentinel",   // 7000ms
  MAINFRAME = "mainframe", // 15000ms

  // MCTS AI - Alchemist
  JESTER = "jester",       // 2000ms
  ALCHEMIST = "alchemist", // 7000ms
  ORACLE = "oracle",       // 15000ms

  // Hybrid AI - Zenith
  NADIR = "nadir",         // 2000ms
  NEXUS = "nexus",         // 7000ms
  ZENITH = "zenith",       // 15000ms
}

export interface AIVariantInfo {
  variant: AIVariant;
  baseType: AIPlayerType;
  displayName: string;
  timeLimit: number; // in milliseconds
  description: string;
  row: number; // For UI grid layout (0-3)
  col: number; // For UI grid layout (0-2)
}

/**
 * Mapping of AI variants to their configuration
 */
export const AI_VARIANT_CONFIG: Record<AIVariant, AIVariantInfo> = {
  // Player
  [AIVariant.PLAYER]: {
    variant: AIVariant.PLAYER,
    baseType: AIPlayerType.PLAYER,
    displayName: "Player",
    timeLimit: 0,
    description: "Human player",
    row: 0,
    col: 0,
  },

  // Priority AI - Row 0
  [AIVariant.FLORA]: {
    variant: AIVariant.FLORA,
    baseType: AIPlayerType.DIONS_AI,
    displayName: "Flora",
    timeLimit: 2000,
    description: "Quick priority-based AI (2s)",
    row: 0,
    col: 1,
  },
  [AIVariant.PRIMA]: {
    variant: AIVariant.PRIMA,
    baseType: AIPlayerType.DIONS_AI,
    displayName: "Prima",
    timeLimit: 7000,
    description: "Standard priority-based AI (7s)",
    row: 0,
    col: 2,
  },
  [AIVariant.GAIA]: {
    variant: AIVariant.GAIA,
    baseType: AIPlayerType.DIONS_AI,
    displayName: "Gaia",
    timeLimit: 12000,
    description: "Deep priority-based AI (12s)",
    row: 0,
    col: 3,
  },

  // Minimax AI - Row 1
  [AIVariant.DRONE]: {
    variant: AIVariant.DRONE,
    baseType: AIPlayerType.MINIMAX,
    displayName: "Drone",
    timeLimit: 2000,
    description: "Quick tactical minimax (2s)",
    row: 1,
    col: 1,
  },
  [AIVariant.SENTINEL]: {
    variant: AIVariant.SENTINEL,
    baseType: AIPlayerType.MINIMAX,
    displayName: "Sentinel",
    timeLimit: 7000,
    description: "Standard tactical minimax (7s)",
    row: 1,
    col: 2,
  },
  [AIVariant.MAINFRAME]: {
    variant: AIVariant.MAINFRAME,
    baseType: AIPlayerType.MINIMAX,
    displayName: "Mainframe",
    timeLimit: 12000,
    description: "Deep tactical minimax (12s)",
    row: 1,
    col: 3,
  },

  // MCTS AI - Row 2
  [AIVariant.JESTER]: {
    variant: AIVariant.JESTER,
    baseType: AIPlayerType.MCTS,
    displayName: "Jester",
    timeLimit: 2000,
    description: "Quick MCTS exploration (2s)",
    row: 2,
    col: 1,
  },
  [AIVariant.ALCHEMIST]: {
    variant: AIVariant.ALCHEMIST,
    baseType: AIPlayerType.MCTS,
    displayName: "Alchemist",
    timeLimit: 7000,
    description: "Standard MCTS exploration (7s)",
    row: 2,
    col: 2,
  },
  [AIVariant.ORACLE]: {
    variant: AIVariant.ORACLE,
    baseType: AIPlayerType.MCTS,
    displayName: "Oracle",
    timeLimit: 12000,
    description: "Deep MCTS exploration (12s)",
    row: 2,
    col: 3,
  },

  // Hybrid AI - Row 3
  [AIVariant.NADIR]: {
    variant: AIVariant.NADIR,
    baseType: AIPlayerType.ULTIMATE,
    displayName: "Nadir",
    timeLimit: 2000,
    description: "Quick hybrid AI (2s)",
    row: 3,
    col: 1,
  },
  [AIVariant.NEXUS]: {
    variant: AIVariant.NEXUS,
    baseType: AIPlayerType.ULTIMATE,
    displayName: "Nexus",
    timeLimit: 7000,
    description: "Standard hybrid AI (7s)",
    row: 3,
    col: 2,
  },
  [AIVariant.ZENITH]: {
    variant: AIVariant.ZENITH,
    baseType: AIPlayerType.ULTIMATE,
    displayName: "Zenith",
    timeLimit: 12000,
    description: "Ultimate hybrid AI (12s)",
    row: 3,
    col: 3,
  },
};

/**
 * Get the base AI type for a variant
 */
export function getBaseAIType(variant: AIVariant): AIPlayerType {
  return AI_VARIANT_CONFIG[variant].baseType;
}

/**
 * Get the time limit for a variant
 */
export function getTimeLimit(variant: AIVariant): number {
  return AI_VARIANT_CONFIG[variant].timeLimit;
}

/**
 * Get display name for a variant
 */
export function getDisplayName(variant: AIVariant): string {
  return AI_VARIANT_CONFIG[variant].displayName;
}

/**
 * Get all variants for a specific base AI type
 */
export function getVariantsForBaseType(baseType: AIPlayerType): AIVariant[] {
  return Object.values(AIVariant).filter(
    variant => AI_VARIANT_CONFIG[variant].baseType === baseType
  );
}

/**
 * Get the grid layout for all AI variants (for UI rendering)
 * Returns a 4x4 grid where [row][col] = AIVariant
 */
export function getAIGridLayout(): (AIVariant | null)[][] {
  const grid: (AIVariant | null)[][] = [
    [AIVariant.PLAYER, AIVariant.FLORA, AIVariant.PRIMA, AIVariant.GAIA],
    [null, AIVariant.DRONE, AIVariant.SENTINEL, AIVariant.MAINFRAME],
    [null, AIVariant.JESTER, AIVariant.ALCHEMIST, AIVariant.ORACLE],
    [null, AIVariant.NADIR, AIVariant.NEXUS, AIVariant.ZENITH],
  ];
  return grid;
}

/**
 * Get row labels for the grid
 */
export function getRowLabels(): string[] {
  return ["Instinct", "Tactical", "Explorer", "Hybrid"];
}

/**
 * Get column labels for the grid
 */
export function getColumnLabels(): string[] {
  return ["", "Quick", "Standard", "Deep"];
}

// ===== NEW SYSTEM: Separate Time Frames and Models =====

export interface AIModelInfo {
  model: AIModel;
  baseType: AIPlayerType;
  displayName: string;
  description: string;
  imagePath: string;
  minimaxModel?: MinimaxModel; // For minimax-based AIs
}

export interface TimeFrameInfo {
  timeFrame: TimeFrame;
  displayName: string;
  timeLimit: number;
  description: string;
}

/**
 * AI Model configurations
 */
export const AI_MODEL_CONFIG: Record<AIModel, AIModelInfo> = {
  [AIModel.ZSTR_BLIND]: {
    model: AIModel.ZSTR_BLIND,
    baseType: AIPlayerType.ZSTR_BLIND,
    displayName: "ZSTR Blind",
    description: "King-blind Astra: considers every plausible enemy king",
    imagePath: "",
  },
  [AIModel.ZSTR]: {
    model: AIModel.ZSTR,
    baseType: AIPlayerType.ZSTR,
    displayName: "ZSTR",
    description: "Zodiac Astra: strategic alpha-beta search with production game rules",
    imagePath: "",
  },
  [AIModel.GAIA]: {
    model: AIModel.GAIA,
    baseType: AIPlayerType.DIONS_AI,
    displayName: "Gaia",
    description: "Priority-based instinctive decision making",
    imagePath: "/Icons/creature-rounded.png",
  },
  [AIModel.SENTINEL]: {
    model: AIModel.SENTINEL,
    baseType: AIPlayerType.MINIMAX,
    displayName: "Sentinel",
    description: "Tactical minimax analysis",
    imagePath: "/Icons/Sentinel.png",
    minimaxModel: MinimaxModel.SENTINEL,
  },
  [AIModel.ALCHEMIST]: {
    model: AIModel.ALCHEMIST,
    baseType: AIPlayerType.MCTS,
    displayName: "Alchemist",
    description: "Monte Carlo tree search exploration",
    imagePath: "/Icons/alchemist.png",
  },
  [AIModel.ZENITH]: {
    model: AIModel.ZENITH,
    baseType: AIPlayerType.ULTIMATE,
    displayName: "Zenith",
    description: "Hybrid ultimate AI",
    imagePath: "/Icons/zenith.png",
  },
  [AIModel.TRAPPER]: {
    model: AIModel.TRAPPER,
    baseType: AIPlayerType.MINIMAX,
    displayName: "Trapper",
    description: "Coming soon...",
    imagePath: "/Icons/trapper.png",
    minimaxModel: MinimaxModel.TRAPPER,
  },
  [AIModel.HUNTER]: {
    model: AIModel.HUNTER,
    baseType: AIPlayerType.MINIMAX,
    displayName: "Hunter",
    description: "Predator AI: targets vulnerable pieces through coordinated attacks",
    imagePath: "/Icons/hunter.png",
    minimaxModel: MinimaxModel.HUNTER,
  },
  [AIModel.WISP]: {
    model: AIModel.WISP,
    baseType: AIPlayerType.WISP,
    displayName: "Wisp",
    description: "Race-focused AI that sacrifices pieces to rush its king home",
    imagePath: "/Icons/wisp.png",
  },
  [AIModel.WEAVER]: {
    model: AIModel.WEAVER,
    baseType: AIPlayerType.ULTIMATE, // Placeholder - update when implemented
    displayName: "Weaver",
    description: "Coming soon...",
    imagePath: "/Icons/weaver.png",
  },
  [AIModel.CREATURE]: {
    model: AIModel.CREATURE,
    baseType: AIPlayerType.CREATURE,
    displayName: "Creature",
    description: "Priority heuristics with minimax tactical fallback",
    imagePath: "/Icons/creature-rounded.png",
  },
  [AIModel.ARROW]: {
    model: AIModel.ARROW,
    baseType: AIPlayerType.MINIMAX,
    displayName: "Arrow",
    description: "Race-focused minimax — rushes king to victory with deep king-line analysis",
    imagePath: "/Icons/arrow.png",
    minimaxModel: MinimaxModel.ARROW,
  },
};

/**
 * Time frame configurations
 */
export const TIME_FRAME_CONFIG: Record<TimeFrame, TimeFrameInfo> = {
  [TimeFrame.FAST]: {
    timeFrame: TimeFrame.FAST,
    displayName: "Fast",
    timeLimit: 2000,
    description: "Quick moves (2s)",
  },
  [TimeFrame.STANDARD]: {
    timeFrame: TimeFrame.STANDARD,
    displayName: "Standard",
    timeLimit: 7000,
    description: "Balanced play (7s)",
  },
  [TimeFrame.DEEP]: {
    timeFrame: TimeFrame.DEEP,
    displayName: "Deep",
    timeLimit: 12000,
    description: "Maximum analysis (12s)",
  },
};

/**
 * Get translation key for AI model
 */
export function getAIModelTranslationKey(model: AIModel): string {
  const keyMap: Record<AIModel, string> = {
    [AIModel.ZSTR]: 'challengeAI.aiZstr',
    [AIModel.ZSTR_BLIND]: 'ZSTR Blind',
    [AIModel.GAIA]: 'challengeAI.aiGaia',
    [AIModel.SENTINEL]: 'challengeAI.aiSentinel',
    [AIModel.ALCHEMIST]: 'challengeAI.aiAlchemist',
    [AIModel.ZENITH]: 'challengeAI.aiZenith',
    [AIModel.TRAPPER]: 'challengeAI.aiTrapper',
    [AIModel.HUNTER]: 'challengeAI.aiHunter',
    [AIModel.WISP]: 'challengeAI.aiWisp',
    [AIModel.WEAVER]: 'challengeAI.aiWeaver',
    [AIModel.CREATURE]: 'challengeAI.aiCreature',
    [AIModel.ARROW]: 'challengeAI.aiArrow',
  };
  return keyMap[model];
}

/**
 * Get translation key for time frame
 */
export function getTimeFrameTranslationKey(timeFrame: TimeFrame): string {
  const keyMap: Record<TimeFrame, string> = {
    [TimeFrame.FAST]: 'challengeAI.timeFast',
    [TimeFrame.STANDARD]: 'challengeAI.timeStandard',
    [TimeFrame.DEEP]: 'challengeAI.timeDeep',
  };
  return keyMap[timeFrame];
}

/**
 * Get base AI type from model
 */
export function getBaseAITypeFromModel(model: AIModel): AIPlayerType {
  return AI_MODEL_CONFIG[model].baseType;
}

/**
 * Get time limit from time frame
 */
export function getTimeLimitFromFrame(timeFrame: TimeFrame): number {
  return TIME_FRAME_CONFIG[timeFrame].timeLimit;
}

/**
 * Convert model + time frame to legacy variant (for backward compatibility)
 */
export function modelAndTimeToVariant(model: AIModel, timeFrame: TimeFrame): AIVariant {
  const mapping: Record<string, AIVariant> = {
    // Gaia variants
    "gaia-fast": AIVariant.FLORA,
    "gaia-standard": AIVariant.PRIMA,
    "gaia-deep": AIVariant.GAIA,

    // Sentinel variants
    "sentinel-fast": AIVariant.DRONE,
    "sentinel-standard": AIVariant.SENTINEL,
    "sentinel-deep": AIVariant.MAINFRAME,

    // Alchemist variants
    "alchemist-fast": AIVariant.JESTER,
    "alchemist-standard": AIVariant.ALCHEMIST,
    "alchemist-deep": AIVariant.ORACLE,

    // Zenith variants
    "zenith-fast": AIVariant.NADIR,
    "zenith-standard": AIVariant.NEXUS,
    "zenith-deep": AIVariant.ZENITH,
  };

  const key = `${model}-${timeFrame}`;
  return mapping[key] || AIVariant.PRIMA;
}

/**
 * Convert legacy variant to model + time frame
 */
export function variantToModelAndTime(variant: AIVariant): { model: AIModel; timeFrame: TimeFrame } {
  const mapping: Record<AIVariant, { model: AIModel; timeFrame: TimeFrame }> = {
    // Gaia (Priority AI)
    [AIVariant.FLORA]: { model: AIModel.GAIA, timeFrame: TimeFrame.FAST },
    [AIVariant.PRIMA]: { model: AIModel.GAIA, timeFrame: TimeFrame.STANDARD },
    [AIVariant.GAIA]: { model: AIModel.GAIA, timeFrame: TimeFrame.DEEP },

    // Sentinel (Minimax)
    [AIVariant.DRONE]: { model: AIModel.SENTINEL, timeFrame: TimeFrame.FAST },
    [AIVariant.SENTINEL]: { model: AIModel.SENTINEL, timeFrame: TimeFrame.STANDARD },
    [AIVariant.MAINFRAME]: { model: AIModel.SENTINEL, timeFrame: TimeFrame.DEEP },

    // Alchemist (MCTS)
    [AIVariant.JESTER]: { model: AIModel.ALCHEMIST, timeFrame: TimeFrame.FAST },
    [AIVariant.ALCHEMIST]: { model: AIModel.ALCHEMIST, timeFrame: TimeFrame.STANDARD },
    [AIVariant.ORACLE]: { model: AIModel.ALCHEMIST, timeFrame: TimeFrame.DEEP },

    // Zenith (Hybrid)
    [AIVariant.NADIR]: { model: AIModel.ZENITH, timeFrame: TimeFrame.FAST },
    [AIVariant.NEXUS]: { model: AIModel.ZENITH, timeFrame: TimeFrame.STANDARD },
    [AIVariant.ZENITH]: { model: AIModel.ZENITH, timeFrame: TimeFrame.DEEP },

    // Player
    [AIVariant.PLAYER]: { model: AIModel.GAIA, timeFrame: TimeFrame.STANDARD },
  };

  return mapping[variant] || { model: AIModel.GAIA, timeFrame: TimeFrame.STANDARD };
}
